import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cumulativeZoom } from '../lib/uiZoomCoords'
import { viewportSize, clampIntoView, EDGE_KEEP, BAR_KEEP } from '../lib/windowStore.js'
import { Z } from '../lib/zLayers.js'

/**
 * DraggablePanel — a floating editor window you can drag by its title bar and
 * dock/undock (collapse to just the bar). Used by the Build Navmesh / Bake
 * Lighting panels so they can be moved out of the way while their debug gizmos
 * stay on (the gizmos are gated on the panel being *open*, not expanded).
 *
 * Position + collapsed state persist per `storageKey` so the panel reappears
 * where you left it — validated against the CURRENT viewport on open and on
 * resize, never trusted raw. A position remembered from a wide monitor puts a
 * fixed-position panel past the edge of a smaller one: rendered, invisible, and
 * unreachable, because the title bar is the only handle and it went with it.
 * (windowStore.clampIntoView — the same rule, and the same bug, as the app's
 * FloatingWindow. These are two implementations of one thing; the honest fix is
 * for these two panels to BE FloatingWindows, and this is the cheap half.)
 */
// Used only before the bar has been measured (the very first layout pass, or a
// panel whose ref has not attached). Close to the real height so a first paint
// off the bottom edge is corrected by ~a pixel, not by 200.
const BAR_H_FALLBACK = 36

export default function DraggablePanel({
  title, storageKey, width = 320, onClose, defaultPos, children,
}) {
  const [pos, setPos] = useState(() => loadPos(storageKey, defaultPos, width))
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(storageKey))
  const drag = useRef(null)
  const barRef = useRef(null)

  useEffect(() => { savePanel(storageKey, pos, collapsed) }, [storageKey, pos, collapsed])

  // On open and on every viewport change: pull the panel back on-screen — the
  // windows' own rule (clampIntoView), with the panel's minimums. What has to
  // stay reachable is the BAR, and its height is MEASURED rather than written
  // down: it is set by the bar's own padding and font, so a constant here would
  // be a copy of a CSS value that can change without this file hearing about
  // it. Not a display-relative figure — it is one element's height, the same on
  // every monitor.
  const onScreen = useCallback(() => {
    const { vw, vh, zoom } = viewportSize()
    if (!(vw > 0) || !(vh > 0)) return
    // getBoundingClientRect is in ZOOMED px; pos is in the panel's own units.
    const barH = (barRef.current?.getBoundingClientRect().height ?? 0) / zoom || BAR_H_FALLBACK
    setPos((p) => {
      const r = clampIntoView({ x: p.x, y: p.y, w: width, h: barH }, vw, vh, { minW: width, minH: barH })
      return (r.x === p.x && r.y === p.y) ? p : { x: r.x, y: r.y }
    })
  }, [width])

  useLayoutEffect(() => {
    onScreen()
    window.addEventListener('resize', onScreen)
    return () => window.removeEventListener('resize', onScreen)
  }, [onScreen])

  const onPointerDown = useCallback((e) => {
    // Ignore drags that start on a button in the bar (dock/close).
    if (e.target.closest('[data-panel-btn]')) return
    // The zoom at the panel, read once per drag: pos.x/y are in the panel's own
    // units (it is `position: fixed` under the app root's UI-scale zoom, so
    // left: pos.x paints at pos.x * zoom), while a pointer delta is in screen
    // pixels. Adding one to the other made the panel travel `zoom` times as far
    // as the cursor — 10% too far at the default step, compounding over a long
    // drag until it visibly outran the pointer. Same fix as the window bars
    // (windowStore.dragRect) and the same root cause as the menu offsets.
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, zoom: cumulativeZoom(e.currentTarget) }
    e.currentTarget.setPointerCapture?.(e.pointerId)
    e.stopPropagation()
  }, [pos])

  const onPointerMove = useCallback((e) => {
    if (!drag.current) return
    const z = drag.current.zoom || 1
    const x = drag.current.ox + (e.clientX - drag.current.sx) / z
    const y = drag.current.oy + (e.clientY - drag.current.sy) / z
    // Keep the title bar on-screen — in the panel's units, so the limit is the
    // viewport divided by the zoom. Against a raw innerWidth the clamp believed
    // the viewport was 10% wider than it is and let the panel past the edge.
    const vw = (typeof window !== 'undefined' ? window.innerWidth : 1920) / z
    const vh = (typeof window !== 'undefined' ? window.innerHeight : 1080) / z
    // The windows' own policy minimums (windowStore): how much of a panel the
    // app refuses to let you lose. They were a local 60/30 here — the same rule
    // written down twice, with two answers.
    setPos({
      x: Math.max(0, Math.min(x, vw - EDGE_KEEP)),
      y: Math.max(0, Math.min(y, vh - BAR_KEEP)),
    })
  }, [])

  const onPointerUp = useCallback((e) => {
    drag.current = null
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [])

  return (
    <div
      // Block canvas orbit/zoom while interacting with the panel.
      onPointerDown={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}
      style={{
        // The floating-window band (zLayers): at the old 1100 this sat UNDER
        // the menubar, so dragging it to the top edge — which its own clamp
        // allows — put the title bar behind opaque chrome that swallows clicks.
        position: 'fixed', left: pos.x, top: pos.y, zIndex: Z.WINDOW_BASE, width,
        borderRadius: 10, background: 'rgba(15,15,18,0.96)', border: '1px solid #2a2a2a',
        color: '#e5e7eb', font: '500 12px var(--m-font)',
        boxShadow: '0 6px 24px rgba(0,0,0,0.5)', userSelect: 'none', overflow: 'hidden',
      }}
    >
      <div
        ref={barRef}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'move',
          padding: '8px 10px 8px 12px', background: '#191920',
          borderBottom: collapsed ? 'none' : '1px solid #262626',
        }}
      >
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{title}</span>
        <button data-panel-btn title={collapsed ? 'Undock (expand)' : 'Dock (collapse)'}
          onClick={() => setCollapsed(c => !c)} style={barBtn}>{collapsed ? '▢' : '—'}</button>
        {onClose && (
          <button data-panel-btn title="Close" onClick={onClose} style={barBtn}>✕</button>
        )}
      </div>
      {!collapsed && <div style={{ padding: 14 }}>{children}</div>}
    </div>
  )
}

const barBtn = {
  width: 22, height: 20, lineHeight: '18px', textAlign: 'center', padding: 0,
  borderRadius: 4, border: '1px solid #3a3a3a', background: 'transparent',
  color: '#cbd5e1', cursor: 'pointer', fontSize: 12,
}

function loadPos(key, defaultPos, width) {
  const fallback = defaultPos || {
    x: typeof window !== 'undefined' ? Math.round(window.innerWidth / 2 - width / 2) : 400,
    y: 52,
  }
  try {
    const v = JSON.parse(localStorage.getItem(`aos-panel-${key}`) || 'null')
    if (v && Number.isFinite(v.x) && Number.isFinite(v.y)) return { x: v.x, y: v.y }
  } catch { /* ignore */ }
  return fallback
}
function loadCollapsed(key) {
  try { return JSON.parse(localStorage.getItem(`aos-panel-${key}`) || 'null')?.collapsed === true }
  catch { return false }
}
function savePanel(key, pos, collapsed) {
  try { localStorage.setItem(`aos-panel-${key}`, JSON.stringify({ x: pos.x, y: pos.y, collapsed })) }
  catch { /* ignore */ }
}
